import { createContext, useContext } from 'react'

import { useFeature } from '@fe-toolkit/utility/client/tracking-context'

import { EPPO_GIFFGAFF_BRAND_REFRESH_2026 } from '@/config/eppoFlags'
import { getRuntimeConfig } from '@/config/runtimeConfig'

import type { ChromeFlags } from '@/api/client-tracking-context/getChromeFlags'
import type { Context, ReactNode } from 'react'

/**
 * The server's answer for the fallback chrome, kept by the layout from the server render for the
 * whole visit: fe-common's own decision (chromeFlags) when it was made, otherwise this app's read
 * of the same flags (localChromeFlags). Undefined when the server had neither.
 *
 * A context rather than props because the fallbacks render on two paths and only one can pass
 * props: the FederatedComponentBoundary in layout.tsx could, but the errorLoadRemote registry
 * (mf-offline-handling-plugin.ts) renders them with the remote's own props. Both render inside the
 * layout, so both see this.
 *
 * On the server the offline plugin loads the fallbacks from its own copy of this module, so every
 * copy shares one context through globalThis (fe-modern-template v1.6.1). A context per copy would
 * leave the server-rendered fallback without the layout's answer and break hydration.
 */
const CONTEXT_KEY = Symbol.for('fe-landing.fallbackChromeFlags')
const registry = globalThis as typeof globalThis & { [CONTEXT_KEY]?: Context<ChromeFlags | undefined> }

export const FallbackChromeContext = (registry[CONTEXT_KEY] ??= createContext<ChromeFlags | undefined>(undefined))

/**
 * Only Brand Refresh switches to the feds-ui header and footer, as in fe-common since #63: Black
 * Friday only changes the header's navigation (brand-refresh-2026 alignment, 20 Oct).
 */
export const showsNewChrome = ({ isBrandRefresh }: ChromeFlags) => isBrandRefresh

const isOn = (value: unknown) => value === true || value === 'true'

/**
 * The answer while there is none from CTC: `fallback` until the lookup returns an assignment,
 * and if it fails or has none.
 */
const useLookedUpFlag = (flag: string, fallback: boolean) => {
  const { value } = useFeature(flag)

  return value === null || value === undefined ? fallback : isOn(value)
}

/**
 * The last resort, with no server answer. Brand Refresh starts from
 * ENABLE_BRAND_REFRESH_WITHOUT_CTC, the design to show when CTC can't say (like Portal's
 * giffgaffBrandRefresh2026HeaderFooter), and Black Friday from off. A lookup that answers wins.
 */
const useBrowserChromeFlags = (): ChromeFlags => ({
  isBrandRefresh: useLookedUpFlag(
    EPPO_GIFFGAFF_BRAND_REFRESH_2026,
    getRuntimeConfig().ENABLE_BRAND_REFRESH_WITHOUT_CTC === 'true'
  ),
  // Hardcoded on for now rather than read from CTC.
  isBlackFriday: true,
})

const BrowserChromeFlags = ({ children }: { children: (flags: ChromeFlags) => ReactNode }) => (
  <>{children(useBrowserChromeFlags())}</>
)

/**
 * Hands the fallback chrome its flags. A server answer is final, as it is for fe-common's own
 * chrome, so the server render is right and nothing switches after load. Only without one does
 * it look the flags up in the browser, and only then does it make that lookup.
 */
export const FallbackChromeFlags = ({ children }: { children: (flags: ChromeFlags) => ReactNode }) => {
  const serverFlags = useContext(FallbackChromeContext)

  return serverFlags ? <>{children(serverFlags)}</> : <BrowserChromeFlags>{children}</BrowserChromeFlags>
}
